import {
  UserAgent,
  Registerer,
  RegistererState,
  SessionState,
  Inviter,
} from 'sip.js';

// GK-HUB: removidos defaults hardcoded (qa-chat / gkhub.local).
// Server e domain DEVEM vir das credenciais do backend (CredentialsController).
// Falhar cedo é melhor do que registrar em outro servidor por engano.

function getOrCreateAudioElement() {
  let audio = document.getElementById('gk-sip-remote-audio');
  if (!audio) {
    audio = document.createElement('audio');
    audio.id = 'gk-sip-remote-audio';
    audio.autoplay = true;
    document.body.appendChild(audio);
  }
  return audio;
}

function detachRemoteAudio() {
  const audio = document.getElementById('gk-sip-remote-audio');
  if (audio) audio.srcObject = null;
}

class SipVoiceClient extends EventTarget {
  constructor() {
    super();
    this.userAgent = null;
    this.registerer = null;
    this.currentSession = null;
    this.initialized = false;
    this.domain = null;
    this.peerConn = null;
    this.isMuted = false;
    this.isHeld = false;
  }

  attachAudioToSession(session) {
    const sdh = session?.sessionDescriptionHandler;
    if (!sdh?.peerConnection) return;

    this.peerConn = sdh.peerConnection;

    const audio = getOrCreateAudioElement();
    const remoteStream = new MediaStream();
    audio.srcObject = remoteStream;

    this.peerConn.getReceivers().forEach(({ track }) => {
      if (track) remoteStream.addTrack(track);
    });

    this.peerConn.addEventListener('track', ({ track }) => {
      remoteStream.addTrack(track);
    });

    audio.play().catch(e => {
      // eslint-disable-next-line no-console
      console.warn('[SipVoiceClient] audio.play() failed:', e);
    });
  }

  cleanupSession() {
    detachRemoteAudio();
    this.peerConn = null;
    this.isMuted = false;
    this.isHeld = false;
    this.currentSession = null;
    this.dispatchEvent(new CustomEvent('call:disconnected'));
  }

  async initialize({ username, password, server, domain, iceServers }) {
    if (this.initialized) return;

    // GK-HUB: valida campos obrigatórios em vez de cair em defaults silenciosos.
    if (!server) throw new Error('SIP server (ws_url) is required');
    if (!domain) throw new Error('SIP domain is required');
    if (!username) throw new Error('SIP username is required');

    this.domain = domain;

    const uri = UserAgent.makeURI(`sip:${username}@${domain}`);
    if (!uri) throw new Error(`Invalid SIP URI for user: ${username}`);

    const resolvedIceServers = iceServers || [{ urls: `stun:${domain}:3478` }];

    this.userAgent = new UserAgent({
      uri,
      authorizationPassword: password,
      authorizationUsername: username,
      transportOptions: { server },
      // GK-HUB: SIP.js >= 0.20 espera iceServers dentro de
      // peerConnectionConfiguration (RTCConfiguration). No top-level era
      // ignorado silenciosamente, fazendo o browser usar config vazia —
      // sem TURN, áudio não atravessa firewalls com UDP bloqueado.
      sessionDescriptionHandlerFactoryOptions: {
        constraints: { audio: true, video: false },
        peerConnectionConfiguration: {
          iceServers: resolvedIceServers,
        },
      },
      delegate: {
        onInvite: invitation => {
          this.currentSession = invitation;

          invitation.stateChange.addListener(state => {
            if (state === SessionState.Established) {
              this.attachAudioToSession(invitation);
            }
            if (state === SessionState.Terminated) {
              this.cleanupSession();
            }
          });

          const callerNumber =
            invitation.remoteIdentity?.uri?.user || 'Desconhecido';
          this.dispatchEvent(
            new CustomEvent('call:incoming', {
              detail: { invitation, callerNumber },
            })
          );
        },
      },
    });

    await this.userAgent.start();

    this.registerer = new Registerer(this.userAgent);

    // Registerer.register() resolves as soon as the REGISTER request is sent
    // over the wire — not when the server accepts or rejects it. Waiting for
    // the first terminal state via stateChange means callers can trust the
    // resolved promise to mean "actually registered" and not "REGISTER was
    // dispatched, good luck". Without this, a 401 from the server turned into
    // "registered: true" in the voice store while the socket was really dead.
    const registrationSettled = new Promise((resolve, reject) => {
      const onState = state => {
        if (state === RegistererState.Registered) {
          this.registerer.stateChange.removeListener(onState);
          resolve();
        } else if (
          state === RegistererState.Unregistered ||
          state === RegistererState.Terminated
        ) {
          this.registerer.stateChange.removeListener(onState);
          reject(new Error(`SIP registration rejected: ${state}`));
        }
      };
      this.registerer.stateChange.addListener(onState);
    });

    await this.registerer.register();
    await registrationSettled;

    this.initialized = true;
  }

  async answer() {
    if (!this.currentSession) return false;
    try {
      await this.currentSession.accept({
        sessionDescriptionHandlerOptions: {
          constraints: { audio: true, video: false },
        },
      });

      // Fallback: attach audio in case Established fired before we could listen
      if (!this.peerConn) {
        this.attachAudioToSession(this.currentSession);
      }

      return true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[SipVoiceClient] answer failed:', e);
      return false;
    }
  }

  async call(target) {
    if (!this.userAgent || !this.initialized) {
      throw new Error('SipVoiceClient is not initialized');
    }
    if (this.currentSession) {
      throw new Error('Already in an active call');
    }

    const targetURI = UserAgent.makeURI(`sip:${target}@${this.domain}`);
    if (!targetURI) {
      throw new Error(`Invalid SIP URI for target: ${target}`);
    }

    const inviter = new Inviter(this.userAgent, targetURI, {
      sessionDescriptionHandlerOptions: {
        constraints: { audio: true, video: false },
      },
    });

    this.currentSession = inviter;

    inviter.stateChange.addListener(state => {
      // GK-HUB: dispara call:ringing pro widget tocar ringback do caller.
      // SIP.js entra em Establishing assim que INVITE sai (e geralmente quando
      // chega 100/180 do callee). Esse é o tempo de "trim-trim" no caller.
      if (state === SessionState.Establishing) {
        this.dispatchEvent(
          new CustomEvent('call:ringing', { detail: { target } })
        );
      }
      if (state === SessionState.Established) {
        this.attachAudioToSession(inviter);
        this.dispatchEvent(
          new CustomEvent('call:established', { detail: { target } })
        );
      }
      if (state === SessionState.Terminated) {
        this.cleanupSession();
      }
    });

    await inviter.invite();

    return inviter;
  }

  toggleMute() {
    if (!this.peerConn) return false;
    this.isMuted = !this.isMuted;
    this.peerConn.getSenders().forEach(sender => {
      if (sender.track && sender.track.kind === 'audio') {
        sender.track.enabled = !this.isMuted;
      }
    });
    return this.isMuted;
  }

  toggleHold() {
    if (!this.peerConn) return false;
    this.isHeld = !this.isHeld;
    this.peerConn.getSenders().forEach(sender => {
      if (sender.track && sender.track.kind === 'audio') {
        sender.track.enabled = !this.isHeld;
      }
    });
    const audio = document.getElementById('gk-sip-remote-audio');
    if (audio) audio.muted = this.isHeld;
    return this.isHeld;
  }

  sendDtmf(digit) {
    if (
      !this.currentSession ||
      this.currentSession.state !== SessionState.Established
    )
      return;
    try {
      this.currentSession.info({
        requestOptions: {
          body: {
            contentDisposition: 'render',
            contentType: 'application/dtmf-relay',
            content: `Signal=${digit}\r\nDuration=160\r\n`,
          },
        },
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[SipVoiceClient] sendDtmf failed:', e);
    }
  }

  transfer(target) {
    if (
      !this.currentSession ||
      this.currentSession.state !== SessionState.Established
    )
      return false;
    try {
      const referTo = UserAgent.makeURI(`sip:${target}@${this.domain}`);
      if (!referTo) return false;
      this.currentSession.refer(referTo);
      return true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[SipVoiceClient] transfer failed:', e);
      return false;
    }
  }

  hangup() {
    if (!this.currentSession) return;
    try {
      const { state } = this.currentSession;
      if (state === SessionState.Established) {
        this.currentSession.bye();
      } else if (typeof this.currentSession.cancel === 'function') {
        this.currentSession.cancel();
      } else if (typeof this.currentSession.reject === 'function') {
        this.currentSession.reject();
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[SipVoiceClient] hangup failed:', e);
    }
    this.currentSession = null;
  }

  async destroy() {
    this.initialized = false;
    this.domain = null;
    this.peerConn = null;
    this.isMuted = false;
    this.isHeld = false;
    try {
      if (this.registerer) await this.registerer.unregister();
      if (this.userAgent) await this.userAgent.stop();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[SipVoiceClient] destroy failed:', e);
    }
  }
}

export default new SipVoiceClient();
